/**
 * 🔔 Raid History Modal — full list of raids this player was involved in.
 *
 * Two tabs:
 *   • RECEIVED — castles attacked YOUR keep (defender perspective)
 *   • SENT     — raids YOU launched (attacker perspective)
 *
 * Each row shows: outcome icon, defender/attacker name, level, what was
 * taken (Chits / Spirit / Trophies), and a "Xh ago" relative timestamp.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { X, ShieldOff, Shield, Send, HandHelping } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RaidHistoryModal({ onClose }) {
  const [role, setRole] = useState("received");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // 🤝 Proxy raid state
  const [myRequest, setMyRequest] = useState(null);
  const [openCalls, setOpenCalls] = useState([]);
  const [sendingFor, setSendingFor] = useState(null);   // history_id currently sending raven for
  const [acceptingId, setAcceptingId] = useState(null); // request_id being accepted

  const refresh = async () => {
    setLoading(true);
    try {
      const calls = ["received", "sent"].includes(role)
        ? axios.get(`${API}/castle/raid-history`, { params: { role, limit: 25 } })
        : Promise.resolve({ data: { events: [], count: 0, unseen: 0 } });
      const [hist, mine, opens] = await Promise.all([
        calls,
        axios.get(`${API}/castle/proxy-raid/my-request`),
        axios.get(`${API}/castle/proxy-raid/open-calls`),
      ]);
      setData(hist.data);
      setMyRequest(mine.data?.request || null);
      setOpenCalls(opens.data?.calls || []);
    } catch {
      setData({ events: [], error: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [role]);

  // 🤝 Send Raven — defender posts a Call for Aid against the attacker who hurt them.
  const onSendRaven = async (ev) => {
    if (myRequest) {
      toast.error("You already have an open Call for Aid — cancel it first.");
      return;
    }
    const ok = window.confirm(
      `🤝 Send a Raven asking your friends + guild-mates to raid @${ev.attacker_handle} on your behalf?\n\n` +
      `• Your bubble stays UP.\n` +
      `• The proxy keeps the Chits + Brawler Spirit.\n` +
      `• You get the Trophies (the satisfaction).\n` +
      `• Open for 2 hours, then expires.`
    );
    if (!ok) return;
    setSendingFor(ev.history_id);
    try {
      await axios.post(`${API}/castle/proxy-raid/request`, { target_id: ev.attacker_id });
      toast.success("🤝 Raven sent. Friends and guild-mates can now answer the call.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the raven.");
    } finally {
      setSendingFor(null);
    }
  };

  const onCancelMyRequest = async () => {
    if (!window.confirm("Cancel your open Call for Aid?")) return;
    try {
      await axios.post(`${API}/castle/proxy-raid/cancel`);
      toast.success("Call cancelled.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  };

  const onAcceptCall = async (call) => {
    setAcceptingId(call.request_id);
    try {
      await axios.post(`${API}/castle/proxy-raid/accept`, { request_id: call.request_id });
      toast.success(`⚔️ Riding out on behalf of @${call.requestor_handle}...`);
      window.location.href = "/battle?return_to=/castle";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept the call.");
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <div
      data-testid="raid-history-modal"
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl border-2 border-amber-700/50 bg-gradient-to-br from-amber-950/40 via-black to-red-950/30 p-5 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Castle Raid Inbox</div>
            <h2 className="text-xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🔔 Raid History
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="raid-history-close"
            className="p-2 hover:bg-red-900/30 rounded transition"
          >
            <X size={18} className="text-neutral-400" />
          </button>
        </div>

        {/* 🤝 Active Call for Aid + Open Calls panel */}
        <ProxyRaidPanel
          myRequest={myRequest}
          openCalls={openCalls}
          acceptingId={acceptingId}
          onCancelMyRequest={onCancelMyRequest}
          onAcceptCall={onAcceptCall}
        />

        {/* Tabs */}
        <div className="flex gap-2 mb-4" data-testid="raid-history-tabs">
          {[
            { id: "received", label: "Received", icon: "🛡️" },
            { id: "sent",     label: "Sent",     icon: "⚔️" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setRole(t.id)}
              data-testid={`raid-history-tab-${t.id}`}
              className={`flex-1 px-3 py-2 text-xs font-black uppercase tracking-widest border rounded transition ${
                role === t.id
                  ? "border-amber-500 bg-amber-900/30 text-amber-200"
                  : "border-neutral-700 text-neutral-400 hover:border-amber-700/60"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Rows */}
        {loading ? (
          <div className="text-center py-10 text-amber-300/60 animate-pulse text-sm">Loading raid log…</div>
        ) : !data?.events?.length ? (
          <div className="text-center py-10 text-neutral-500 text-sm" data-testid="raid-history-empty">
            {role === "received"
              ? "Your castle has not been raided yet. 🛡️"
              : "You haven't launched any raids yet. ⚔️"}
          </div>
        ) : (
          <div className="space-y-2" data-testid="raid-history-list">
            {data.events.map((ev) => (
              <RaidRow
                key={ev.history_id}
                ev={ev}
                role={role}
                onSendRaven={onSendRaven}
                ravenBusy={sendingFor === ev.history_id}
                myRequestActive={!!myRequest}
              />
            ))}
          </div>
        )}

        <p className="text-[10px] text-neutral-600 text-center mt-4">
          Showing {data?.count || 0} most-recent events.
        </p>
      </div>
    </div>
  );
}

// 🤝 Proxy Raid Panel — only renders if there's an open call (mine or others')
function ProxyRaidPanel({ myRequest, openCalls, acceptingId, onCancelMyRequest, onAcceptCall }) {
  if (!myRequest && !openCalls?.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-purple-700/50 bg-purple-950/20 p-3" data-testid="proxy-raid-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/80 mb-2">
        🤝 Calls for Aid
      </div>
      {myRequest && (
        <div className="rounded border border-amber-700/50 bg-amber-950/30 p-2 mb-2 flex items-start justify-between gap-2" data-testid="proxy-raid-my-request">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-amber-200 font-bold flex items-center gap-1">
              <Send size={11} /> Your raven is in flight
            </div>
            <div className="text-[10px] text-neutral-400 mt-1">
              Target: <b>@{myRequest.target_handle}</b> (Lv {myRequest.target_level}) · {myRequest.target_castle_name}
            </div>
            <div className="text-[10px] text-neutral-500">Expires: {new Date(myRequest.expires_at).toLocaleString()}</div>
          </div>
          <button
            onClick={onCancelMyRequest}
            data-testid="proxy-raid-cancel-btn"
            className="text-[10px] font-bold uppercase px-2 py-1 border border-red-700/60 text-red-300 hover:bg-red-900/30 rounded shrink-0"
          >
            Cancel
          </button>
        </div>
      )}
      {openCalls?.map((c) => (
        <div
          key={c.request_id}
          className="rounded border border-purple-700/50 bg-black/40 p-2 mb-2 flex items-start justify-between gap-2"
          data-testid={`proxy-raid-call-${c.request_id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-xs text-purple-200 font-bold flex items-center gap-1">
              <HandHelping size={11} /> @{c.requestor_handle} calls for aid
            </div>
            <div className="text-[10px] text-neutral-400 mt-1">
              Target: <b>@{c.target_handle}</b> (Lv {c.target_level}) · {c.target_castle_name}
            </div>
            <div className="text-[10px] text-neutral-500 italic">You keep Chits + Spirit · requestor gets Trophies</div>
          </div>
          <button
            onClick={() => onAcceptCall(c)}
            disabled={acceptingId === c.request_id}
            data-testid={`proxy-raid-accept-${c.request_id}`}
            className="text-[10px] font-black uppercase px-2 py-1 border border-amber-600 text-amber-200 hover:bg-amber-900/40 rounded shrink-0 disabled:opacity-50"
          >
            {acceptingId === c.request_id ? "Riding…" : "⚔️ Answer"}
          </button>
        </div>
      ))}
    </div>
  );
}

function RaidRow({ ev, role, onSendRaven, ravenBusy, myRequestActive }) {
  // "received" → outcome shown from DEFENDER perspective:
  //   ev.won = attacker won = defender lost (= we lost)
  // "sent" → outcome shown from ATTACKER perspective:
  //   ev.won = we won
  const youLost = (role === "received") ? ev.won : !ev.won;
  const youWon  = !youLost;

  const other = role === "received"
    ? { name: "by " + (ev.attacker_handle || "?"), level: ev.attacker_level, npc: false }
    : { name: ev.defender_name || "?", level: ev.defender_level, npc: ev.defender_is_npc };

  const bgCls = youWon
    ? "border-emerald-800/50 bg-emerald-950/20"
    : "border-red-800/50 bg-red-950/20";

  // 🤝 Show "Send Raven" only on received-losses against real-player attackers,
  // and only if we don't already have an open Call for Aid.
  const canSendRaven = (role === "received") && ev.won && ev.attacker_id
                       && !myRequestActive && !ev.vengeance;

  return (
    <div
      className={`rounded-lg border ${bgCls} p-3 flex items-start gap-3`}
      data-testid={`raid-history-row-${ev.history_id}`}
    >
      <div className="text-3xl shrink-0">
        {youWon ? <span title="You won this raid">🏆</span> : <span title="You lost this raid">💀</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 text-sm font-bold text-amber-100 truncate">
          {other.npc && (
            <span className="text-[9px] font-black px-1 rounded border border-fuchsia-500/70 bg-fuchsia-950/60 text-fuchsia-200 leading-none shrink-0">
              🤖
            </span>
          )}
          {ev.proxy && ev.via_proxy_handle && role === "sent" && (
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full border border-purple-500/70 bg-purple-950/60 text-purple-200 leading-none shrink-0" title="Vengeance dealt via proxy">
              🤝 via @{ev.via_proxy_handle}
            </span>
          )}
          {ev.proxy && ev.via_requestor_handle && role === "sent" && (
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full border border-purple-500/70 bg-purple-950/60 text-purple-200 leading-none shrink-0" title="Proxy raid on behalf of">
              🤝 for @{ev.via_requestor_handle}
            </span>
          )}
          <span className="truncate">{other.name}</span>
          {other.level != null && other.level > 0 && (
            <span className="text-[10px] text-neutral-500 shrink-0">· Lv {other.level}</span>
          )}
        </div>
        <div className="text-[10px] text-neutral-500 mt-0.5">
          {relativeTime(ev.ts)}
          {ev.bubble_applied && role === "received" && (
            <span className="ml-2 text-sky-300">· 🛡️ Bubble triggered</span>
          )}
          {ev.vengeance && (
            <span className="ml-2 text-purple-300">· 🤝 Vengeance</span>
          )}
        </div>
        {/* Spoils strip — present on both perspectives, sign-flipped on received losses */}
        <div className="flex flex-wrap gap-3 mt-2 text-[11px]">
          {role === "received" ? (
            <ReceivedSpoils ev={ev} youLost={youLost} />
          ) : (
            <SentSpoils ev={ev} youWon={youWon} />
          )}
        </div>
        {/* 🤝 Send Raven button on received-losses */}
        {canSendRaven && onSendRaven && (
          <button
            onClick={() => onSendRaven(ev)}
            disabled={ravenBusy}
            data-testid={`send-raven-${ev.history_id}`}
            className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 border border-purple-500/70 text-purple-200 bg-purple-950/40 hover:bg-purple-900/60 rounded disabled:opacity-50 transition"
            title="Ask a friend or guild-mate to raid them on your behalf"
          >
            {ravenBusy ? "Sending…" : "🤝 Send Raven"}
          </button>
        )}
      </div>
    </div>
  );
}

function ReceivedSpoils({ ev, youLost }) {
  if (!youLost) {
    return (
      <span className="text-emerald-300 flex items-center gap-1">
        <Shield size={11} /> Castle held — nothing taken
      </span>
    );
  }
  return (
    <>
      <span className="text-red-300 flex items-center gap-1">
        <ShieldOff size={11} />
      </span>
      {ev.chits_taken > 0 && (
        <span className="text-red-300">🪙 −{ev.chits_taken.toLocaleString()}</span>
      )}
      <span className="text-neutral-500 italic">
        Their {ev.trophies_won > 0 ? `+${ev.trophies_won} 🏆` : "0 🏆"}, your Brawler Spirit untouched.
      </span>
    </>
  );
}

function SentSpoils({ ev, youWon }) {
  if (!youWon) {
    return <span className="text-red-300">💀 Driven out — no spoils</span>;
  }
  return (
    <>
      {ev.chits_taken > 0 && <span className="text-amber-200">🪙 +{ev.chits_taken.toLocaleString()}</span>}
      {ev.spirit_won > 0 && <span className="text-orange-300">🥊 +{ev.spirit_won}</span>}
      {ev.trophies_won > 0 && <span className="text-yellow-300">🏆 +{ev.trophies_won}</span>}
    </>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const m = Math.round(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch { return ""; }
}
